import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import React, { useState } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HistoryIcon from "@mui/icons-material/History";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import UnifiedFilePreview from "../common/UnifiedFilePreview";
import { getDownloadUrl } from "../../services/api/documents/projectDocumentsApi";

/**
 * Card compacto para cada slot de documento
 */
export default function DocumentSlotCard({
  slot,
  projectId,
  onUploadClick,
  onDownloadClick,
  onWhatsAppClick,
  onHistoryClick,
  onDeleteClick,
}) {
  const theme = useTheme();
  const [dragOver, setDragOver] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const { documentType, currentDocument, hasDocument } = slot;
  const isComplete = hasDocument && currentDocument;

  // Handlers de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelected(files[0]);
    }
  };

  // Validação de arquivo
  const handleFileSelected = (file) => {
    if (!file) return;

    const extension = file.name.split(".").pop().toLowerCase();
    if (!documentType.allowedExtensions.includes(extension)) {
      toast.error(
        `Extensão não permitida. Aceitas: ${documentType.allowedExtensions.join(", ")}`
      );
      return;
    }

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > documentType.maxSizeMB) {
      toast.error(`Arquivo muito grande. Máximo: ${documentType.maxSizeMB}MB`);
      return;
    }

    if (onUploadClick) {
      onUploadClick(documentType, file);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
  };

  const fetchDownloadUrl = async () => {
    const response = await getDownloadUrl(currentDocument.id);
    return response.downloadUrl;
  };

  const handleDownload = async () => {
    if (!currentDocument) return;
    try {
      setDownloading(true);
      const url = await fetchDownloadUrl();
      if (url) {
        const anchor = document.createElement("a");
        anchor.href = url;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        toast.success("Download iniciado!");
      } else {
        toast.error("URL de download não disponível");
      }
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      toast.error("Erro ao fazer download do documento");
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = async () => {
    if (!currentDocument) return;
    try {
      const url = await fetchDownloadUrl();
      if (url) {
        setPreviewUrl(url);
        setPreviewOpen(true);
      } else {
        toast.error("URL de visualização não disponível");
      }
    } catch (error) {
      console.error("Erro ao obter URL de visualização:", error);
      toast.error("Erro ao abrir visualização");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  };

  const canPreview = (fileName) => {
    if (!fileName) return false;
    const ext = fileName.toLowerCase();
    return /\.(pdf|jpg|jpeg|png|gif|webp|bmp)$/i.test(ext);
  };

  // Tooltip com informações detalhadas
  const detailsTooltip = isComplete ? (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" display="block" sx={{ fontWeight: 600 }}>
        {currentDocument.fileName}
      </Typography>
      <Typography variant="caption" display="block">
        Versão: {currentDocument.version} | {formatFileSize(currentDocument.fileSize)}
      </Typography>
      <Typography variant="caption" display="block">
        Por: {currentDocument.uploadedBy?.name || "Desconhecido"}
      </Typography>
      <Typography variant="caption" display="block">
        Em: {formatDate(currentDocument.uploadDate)}
      </Typography>
    </Box>
  ) : (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" display="block">
        Extensões: {documentType.allowedExtensions.join(", ").toUpperCase()}
      </Typography>
      <Typography variant="caption" display="block">
        Tamanho máx: {documentType.maxSizeMB} MB
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        height: 52,
        borderRadius: 2,
        border: `1px solid ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
        bgcolor: dragOver ? theme.palette.action.hover : theme.palette.background.default,
        transition: "all 0.15s ease",
        cursor: "pointer",
        "&:hover": {
          bgcolor: theme.palette.action.hover,
        },
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => {
        if (previewOpen) return;
        if (isComplete && canPreview(currentDocument?.fileName)) {
          handlePreview();
        } else if (!isComplete) {
          document.getElementById(`file-input-${documentType.id}`).click();
        }
      }}
    >
      {/* Status icon */}
      {isComplete ? (
        <CheckCircleIcon sx={{ fontSize: 20, color: theme.palette.success.main, flexShrink: 0 }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: theme.palette.grey[400], flexShrink: 0 }} />
      )}

      {/* Nome + categoria */}
      <Tooltip title={detailsTooltip} arrow>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "0.82rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {documentType.name}
          </Typography>
          {isComplete && currentDocument && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "text.secondary",
                fontSize: "0.72rem",
              }}
            >
              {currentDocument.fileName}
            </Typography>
          )}
        </Box>
      </Tooltip>

      {/* Ações */}
      {isComplete ? (
        <Box sx={{ display: "flex", gap: 0.25, flexShrink: 0 }}>
          {canPreview(currentDocument?.fileName) && (
            <Tooltip title="Visualizar">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handlePreview(); }}
                sx={{ p: 0.5 }}
                color="primary"
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Baixar">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleDownload(); }}
              disabled={downloading}
              sx={{ p: 0.5 }}
            >
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="WhatsApp">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onWhatsAppClick && onWhatsAppClick(currentDocument); }}
              sx={{ p: 0.5 }}
            >
              <WhatsAppIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Histórico">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onHistoryClick && onHistoryClick(documentType); }}
              sx={{ p: 0.5 }}
            >
              <HistoryIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deletar">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDeleteClick && onDeleteClick(currentDocument); }}
              color="error"
              sx={{ p: 0.5 }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Tooltip title={dragOver ? "Solte aqui" : "Clique ou arraste"}>
          <CloudUploadIcon sx={{ fontSize: 22, color: theme.palette.primary.main, flexShrink: 0 }} />
        </Tooltip>
      )}

      {/* Input escondido */}
      <input
        id={`file-input-${documentType.id}`}
        type="file"
        accept={documentType.allowedExtensions.map(ext => `.${ext}`).join(",")}
        style={{ display: "none" }}
        onChange={handleFileInput}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Modal de Preview */}
      {isComplete && currentDocument && (
        <UnifiedFilePreview
          open={previewOpen}
          onClose={() => { setPreviewOpen(false); setPreviewUrl(null); }}
          fileName={currentDocument.fileName}
          fileUrl={previewUrl}
        />
      )}
    </Box>
  );
}
